import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { PollResultResponse } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import { CreatePollRequest, UpdatePollRequest, ViewPollResultsRequest, ViewPollResultsResponse } from './poll.types';

import { CREATING_POLL, RETRIEVING_POLL_RESULTS, UPDATING_POLL } from './poll.constants';

import { requestCreatePoll, requestUpdatePoll, requestViewPollResults } from './poll.actions';

import { getCreatePollRequest, getUpdatePollRequest, getViewPollResultsRequest } from './poll.endpoints';

import { setPollResults } from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';

export function* performCreatePollRequest({ payload }: SagaPayload<CreatePollRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_POLL));

    const [endpoint, options] = getCreatePollRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_POLL));
  }
}

export function* watchForCreatePollRequest() {
  yield takeLatest(requestCreatePoll.type, performCreatePollRequest);
}

export function* performUpdatePollRequest({ payload }: SagaPayload<UpdatePollRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_POLL));

    const [endpoint, options] = getUpdatePollRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_POLL));
  }
}

export function* watchForUpdatePollRequest() {
  yield takeLatest(requestUpdatePoll.type, performUpdatePollRequest);
}

export function* performViewPollResultsRequest({ payload }: SagaPayload<ViewPollResultsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_POLL_RESULTS));

    const [endpoint, options] = getViewPollResultsRequest(requestData);

    const { data }: AxiosResponse<ViewPollResultsResponse> = yield call(axios, endpoint, options);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { message: _message, ...pollData } = data;
    pollData.results.sort((resultOne, resultTwo) => {
      const votes = resultTwo.votes - resultOne.votes;
      if (votes === 0) {
        if (resultTwo.option > resultOne.option) return -1;
        if (resultTwo.option < resultOne.option) return 1;
        return 0;
      }

      return votes;
    });

    yield put(setPollResults(pollData as unknown as PollResultResponse));
    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_POLL_RESULTS));
  }
}

export function* watchForViewPollResultsRequest() {
  yield takeLatest(requestViewPollResults.type, performViewPollResultsRequest);
}

export default function* pollSaga() {
  yield all([watchForCreatePollRequest(), watchForUpdatePollRequest(), watchForViewPollResultsRequest()]);
}
